import {
  type KlaviyoFormVersion,
  type KlaviyoHiddenFieldEntry,
  type KlaviyoBlockData,
  type KlaviyoIdentifyPayload,
  CustomerProcessStatusEnum,
} from '@repo/models/klaviyo';
import { isPixelSupported, PIXELS, waitForKlaviyo } from './pixels';

// Duplicated from `@repo/gatsby-localization-helpers` because sanity-studio imports this the current file and can't depend on the gatsby-specific package due to different compilation targets.
const GLOBAL_LOCALES = {
  English: { id: 'en-us' },
  Australia: { id: 'en-au' },
  EnglishUK: { id: 'en-gb' },
  French: { id: 'fr-fr' },
  FrenchBelgium: { id: 'fr-be' },
  Germany: { id: 'de-de' },
  EnglishIreland: { id: 'en-ie' },
  Italian: { id: 'it-it' },
  EnglishNetherlands: { id: 'en-gb-nl' },
  Spanish: { id: 'es-es' },
  GermanSwitzerland: { id: 'de-ch' },
  EnglishCanada: { id: 'en-ca' },
  FrenchCanada: { id: 'fr-ca' },
} as const;

const TRACK_EVENT_ALLOWLIST = new Set<string>([
  'Form Started',
  'Form Submitted',
  'Form Step Submitted',
  'Form Final Step Submitted',
  'Form completed by profile',
  'Form submitted by profile',
  'prospect_consult_scheduled',
  'prospect_view_calendar',
  'Back in Stock Interest',
]);

export const KLAVIYO_ADDRESS_FIELDS = ['$address1', '$address'] as const;
export const KLAVIYO_EMAIL_FIELD_PATTERN = /email/i;
export const KLAVIYO_PHONE_FIELD_PATTERN = /phone/i;
export const KLAVIYO_REFERRAL_CODE_FIELD_PATTERN = /^referral[_\s]?code$/i;

export const MARKETING_PARAMS = [
  'client_id',
  'hubspot_token',
  'promo',
  'promo_code',
  'promoCode',
  'referral_code',
  'utm_campaign',
  'utm_content',
  'utm_medium',
  'utm_source',
  'utm_term',
  'utm_id',
] as const;

export function sanitizeMarketingParam(raw: string | null): string | null {
  if (!raw) return null;
  const clean = raw
    .trim()
    .replaceAll(/[^\x20-\x7E]/g, '')
    .replaceAll(/[<>]/g, '');
  return clean || null;
}

export function mapSessionMarketingParams(): Record<string, string> {
  if (!sessionStorage) return {};
  const result: Record<string, string> = {};
  for (const param of MARKETING_PARAMS) {
    const value = sanitizeMarketingParam(sessionStorage.getItem(param));
    if (value) result[param] = value;
  }
  return result;
}

export const getDeviceType = (): 'DESKTOP' | 'MOBILE' => {
  if (globalThis.window === undefined) return 'DESKTOP';
  return globalThis.window.matchMedia?.('(max-width: 767px)').matches
    ? 'MOBILE'
    : 'DESKTOP';
};

export const getKlaIdFromCookie = (): string | null => {
  if (typeof document === 'undefined') return null;

  return document.cookie
    .split(';')
    .map(c => c.trim())
    .reduce(
      (acc, curr) => {
        const [key, val] = curr.split('=');
        acc[key] = val;
        return acc;
      },
      {} as Record<string, string>
    ).__kla_id;
};

export const decodeKlaId = (
  klaId: string
): { cid?: string; $exchange_id?: string } | null => {
  try {
    const json = atob(klaId);
    return JSON.parse(json);
  } catch {
    // If the cookie/exchange id is not base64 JSON, ignore and treat as plain id.
    return null;
  }
};

export const getKlaviyoIdsFromCookie = (): {
  cid?: string;
  exchangeId?: string;
  raw?: string;
} => {
  const raw = getKlaIdFromCookie();
  if (!raw) return {};

  const decoded = decodeKlaId(raw) ?? {};
  return {
    cid: decoded?.cid,
    exchangeId: decoded?.$exchange_id,
    raw,
  };
};

export const getKlaviyoCidFromCookie = (): string | null => {
  const ids = getKlaviyoIdsFromCookie();
  return ids.cid ?? null;
};

/**
 * Resolves hidden field entries to a flat key-value record.
 * Static entries use `value`; queryParam entries use the current URL search params.
 */
export function resolveHiddenFields(
  entries: KlaviyoHiddenFieldEntry[] | null | undefined,
  urlSearchParams?: URLSearchParams | null
): Record<string, string> {
  const params =
    urlSearchParams ??
    (typeof globalThis !== 'undefined' &&
    typeof globalThis.location?.search === 'string'
      ? new URLSearchParams(globalThis.location.search)
      : undefined);
  const result: Record<string, string> = {};
  if (!entries?.length) return result;
  for (const entry of entries) {
    const key = entry?.key;
    if (key) {
      if (entry.valueType === 'queryParam' && entry.queryParam && params) {
        result[key] = params.get(entry.queryParam) ?? '';
      } else {
        result[key] = entry.value ?? '';
      }
    }
  }
  return result;
}

export function mergeKlaviyoForm(
  rawBlock: any,
  resolvedMap: Map<string, any>
): any {
  const match = rawBlock?._key ? resolvedMap.get(rawBlock._key) : null;

  const merged = match?.klaviyoForm
    ? { ...rawBlock, klaviyoForm: match.klaviyoForm }
    : rawBlock;

  const mergedKeys = merged ? Object.keys(merged) : [];

  if (mergedKeys?.length > 0) {
    for (const key of mergedKeys) {
      const value = merged[key];

      if (Array.isArray(value)) {
        merged[key] = value.map(child =>
          typeof child === 'object'
            ? mergeKlaviyoForm(child, resolvedMap)
            : child
        );
        // eslint-disable-next-line no-continue
        continue;
      }

      if (value && typeof value === 'object') {
        merged[key] = mergeKlaviyoForm(value, resolvedMap);
      }
    }
  }

  return merged;
}

/**
 * Gets the Klaviyo form name from the block data.
 *
 * Priority:
 * 1. Form-level name from metadata (e.g., "Multi-Step Lead Gen Form V1")
 * 2. Version-level name (e.g., "Default Embed")
 * 3. Form ID as fallback
 *
 * @param block - The Klaviyo block data from Sanity
 * @param liveKlaviyoForm - The live form version data
 * @param formId - The form ID as fallback
 * @returns The form name
 */
export function getKlaviyoFormName(
  block: KlaviyoBlockData | undefined,
  liveKlaviyoForm: any,
  formId: string
): string {
  return block?.klaviyoForm?.fullForm?.name ?? liveKlaviyoForm?.name ?? formId;
}

/**
 * Recursively flattens a nested array of blocks, collecting all blocks that have a `_key` property.
 * Used to extract all blocks from nested structures (e.g., columns, rows, reusable blocks).
 */
export function flatten(blocks: any[]): any[] {
  const out: any[] = [];

  for (const block of blocks) {
    if (!block || typeof block !== 'object') {
      // eslint-disable-next-line no-continue
      continue;
    }

    if (block._key) out.push(block);

    // inspect objects for nested blocks
    for (const key of Object.keys(block)) {
      const value = block[key];
      if (Array.isArray(value)) out.push(...flatten(value));
      else if (value && typeof value === 'object')
        out.push(...flatten([value]));
    }
  }

  return out;
}

export class KlaviyoCaptchaError extends Error {
  captchaUrl?: string;

  response?: Response;

  constructor(
    message: string,
    options?: { captchaUrl?: string; response?: Response }
  ) {
    super(message);
    this.name = 'KlaviyoCaptchaError';
    this.captchaUrl = options?.captchaUrl;
    this.response = options?.response;
  }
}

export const BRAND_KEYS = [
  'ifb',
  'ifbDev',
  'petsafe',
  'sportdog',
  'kurgo',
] as const;
export type BrandKeyLiteral = (typeof BRAND_KEYS)[number];

export function getKlaviyoBrand(): BrandKeyLiteral {
  return (process.env.GATSBY_KLAVIYO_BRAND as BrandKeyLiteral) ?? 'ifb';
}

export function getKlaviyoApiBase(): string {
  return process.env.GATSBY_KLAVIYO_API;
}

export function buildKlaviyoApiUrl(
  action: 'subscribe' | 'track' | 'trackBulk',
  brand?: BrandKeyLiteral
): string {
  const apiBase = getKlaviyoApiBase();
  const brandParam = brand ?? getKlaviyoBrand();
  return `${apiBase}/submit?brand=${brandParam}&action=${action}`;
}

/**
 * Helper to send trackBulk events to Lambda API
 */
async function sendTrackBulkToLambda(
  events: Array<{
    eventName: string;
    values: Record<string, unknown>;
    formId?: string | number;
    formVersion?: string | number;
    locale?: string;
  }>,
  locale: string
): Promise<void> {
  const url = buildKlaviyoApiUrl('trackBulk');
  const body = {
    events,
    locale: locale || 'en-us',
  };

  const res = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `trackBulk failed ${res.status}`);
  }
}

type TrackMetric =
  | 'loadedEmbed'
  | 'viewedStep'
  | 'stepSubmit'
  | 'submitModal'
  | 'submitSpinToWin'
  | 'receivedOutcomeView';

type MetricServiceEventName =
  | 'viewed_form'
  | 'viewed_form_step'
  | 'submitted_form_step'
  | 'engaged_with_form'
  | undefined;

type AnyForm = {
  id?: string | number;
  form_version_id?: string | number;
  form_version_c_id?: string | number;
  form_version_cid?: string | number;
  c_id?: string | number;
  cid?: string | number;
  type?: string;
  properties?: { type?: string };
  steps?: Array<any>;
};

export async function sendOnsiteTrackAnalytics({
  companyId,
  metric,
  metricServiceEventName,
  eventDetails,
  metricGroup = 'signup-forms',
  logToMetricsService = true,
}: {
  companyId: string;
  metric: TrackMetric;
  metricServiceEventName: MetricServiceEventName;
  eventDetails: Record<string, unknown>;
  metricGroup?: string;
  logToMetricsService?: boolean;
}): Promise<void> {
  await fetch(
    `https://a.klaviyo.com/onsite/track-analytics?company_id=${companyId}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
      },
      body: JSON.stringify({
        metric_group: metricGroup,
        events: [
          {
            metric,
            log_to_statsd: true,
            log_to_s3: true,
            log_to_metrics_service: logToMetricsService,
            metric_service_event_name: metricServiceEventName,
            event_details: eventDetails,
          },
        ],
      }),
    }
  );
}

export async function sendFormTrackAnalytics({
  companyId,
  formId,
  liveKlaviyoForm,
  metric,
  metricServiceEventName,
  cid,
  extraEventDetails,
  logToMetricsService = true,
}: {
  companyId?: string;
  formId: string;
  liveKlaviyoForm: KlaviyoFormVersion | AnyForm;
  metric: TrackMetric;
  metricServiceEventName: MetricServiceEventName;
  cid?: string | null;
  extraEventDetails?: Record<string, unknown>;
  logToMetricsService?: boolean;
}): Promise<void> {
  if (globalThis.window === undefined) return;

  const location = globalThis?.location;
  const href = location?.href ?? '';
  const pageUrl = location ? `${location.origin}${location.pathname}` : '';
  const hostname = location?.hostname ?? '';
  const formVersionId = liveKlaviyoForm?.id ?? liveKlaviyoForm?.form_version_id;
  const rawFormType =
    liveKlaviyoForm?.properties?.type ?? liveKlaviyoForm?.type ?? 'EMBED';
  const normalizedFormType =
    typeof rawFormType === 'string' ? rawFormType.toUpperCase() : 'EMBED';
  const formVersionCId = String(
    liveKlaviyoForm?.form_version_c_id ??
      liveKlaviyoForm?.form_version_cid ??
      liveKlaviyoForm?.c_id ??
      liveKlaviyoForm?.cid ??
      '1'
  );

  const resolvedCompanyId =
    companyId ||
    process.env.GATSBY_KLAVIYO_PUBLIC_API_KEY ||
    process.env.NEXT_PUBLIC_KLAVIYO_PUBLIC_API_KEY ||
    'Vnqm4U';

  const eventDetails = {
    form_version_c_id: formVersionCId,
    is_client: true,
    form_id: formId,
    form_version_id: formVersionId,
    form_type: normalizedFormType,
    device_type: getDeviceType(),
    hostname,
    href,
    page_url: pageUrl,
    cid: cid ?? undefined,
    ...extraEventDetails,
  };

  await sendOnsiteTrackAnalytics({
    companyId: resolvedCompanyId,
    metric,
    metricServiceEventName,
    eventDetails,
    logToMetricsService,
  });
}

export async function sendServerTrackEvent({
  eventName,
  properties,
  formId,
  formVersionId,
  locale,
  email,
  phoneNumber,
}: {
  eventName: string;
  properties: Record<string, string | number>;
  formId?: string | number;
  formVersionId?: string | number;
  locale?: string;
  email?: string;
  phoneNumber?: string;
}) {
  if (!TRACK_EVENT_ALLOWLIST.has(eventName)) {
    return;
  }

  const resolvedEmail = email ?? properties?.$email;
  const resolvedPhone = phoneNumber ?? properties?.$phone_number;
  const resolvedLocale = locale || 'en-us';

  const dedupeKey = JSON.stringify({
    eventName,
    formId: formId ?? null,
    formVersionId: formVersionId ?? null,
    properties: {
      StepName: properties?.StepName ?? properties?.step_name ?? null,
      StepIndex: properties?.StepIndex ?? properties?.step_index ?? null,
    },
  });
  const now = Date.now();
  const lastSent = (globalThis as any).__kl_server_track_dedupe?.[dedupeKey];
  if (lastSent && now - lastSent < 2000) return;

  const dedupeStore =
    (globalThis as any).__kl_server_track_dedupe ??
    ((globalThis as any).__kl_server_track_dedupe = {});
  dedupeStore[dedupeKey] = now;

  const payload = {
    eventName,
    values: {
      ...properties,
      $email: resolvedEmail ?? undefined,
      $phone_number: resolvedPhone ?? undefined,
    },
    formId,
    formVersion: formVersionId,
    locale: resolvedLocale,
  };

  try {
    await sendTrackBulkToLambda([payload], resolvedLocale);
  } catch (error) {
    console.error('[Klaviyo] Failed to send track event:', error);
  }
}

export type PageType = 'Landing Page' | 'Article Page';

type TrackEventName =
  | 'Search Viewed'
  | 'Viewed Product'
  | 'Viewed Category'
  | 'Added to Cart'
  | 'Viewed Page'
  | 'Active on Site'
  | 'Form Started'
  | 'Form Submitted'
  | 'prospect_consult_scheduled'
  | 'prospect_view_calendar'
  | 'Back in Stock Interest';

export interface KlaviyoPublicMap {
  publicKey: string;
  newsletterListId: string;
}
export interface ViewedProductPayload {
  ProductName: string;
  shopifyId: string;
  SKU?: string;
  Categories?: any[];
  Category?: string;
  ImageURL?: string;
  Brand?: string;
  Price: number;
  CompareAtPrice?: number;
  URL: string;
  locale: string;
}

export interface ViewedCategoryPayload {
  CategoryName: string;
  URL: string;
  locale: string;
}

export interface SearchViewedPayload {
  SearchTerm: string;
  ResultCount: number;
  Timestamp: string; // ISO
  locale: string;
}

export interface AddedItemEntry {
  ProductID: string;
  VariantID?: string;
  SKU?: string;
  ProductName: string;
  Quantity: number;
  ItemPrice: string;
  Brand: string;
  ImageURL?: string;
  ProductCategories?: string[];
}

export interface AddedToCartPayload {
  $value: number;
  AddedItemProductName: string;
  AddedItemProductID: string;
  AddedItemSKU?: string;
  AddedItemURL?: string;
  AddedItemPrice: number;
  AddedItemQuantity: number;
  ItemNames: string[];
  Items: AddedItemEntry[];
  CheckoutURL?: string;
  locale: string;
}

export interface ViewedPagePayload {
  PageName: string;
  ViewedPageUrl: string;
  PageType: PageType;
  CustomerContext?: Record<string, unknown>;
  locale: string;
}

export interface BackInStockInterestPayload {
  email: string;
  ProductID: string;
  SKU?: string;
  ProductName: string;
  ProductURL: string;
  Price: number | string;
  VariantName?: string;
  VariantID: string;
  Locale: string;
}

/**
 * Use getKlaviyoBrand() instead. This function is kept for backward compatibility.
 */
export function currentBrand(): BrandKeyLiteral {
  return getKlaviyoBrand();
}

export function brandLabel(brand: BrandKeyLiteral = currentBrand()): string {
  if (brand === 'ifb') return 'Invisible Fence Brand';
  return brand;
}

function kurgoLocaleToKlaviyoMap(locale: string): KlaviyoPublicMap {
  const defaultPublicKey =
    process.env.SANITY_STUDIO_KLAVIYO_PUBLIC_API_KEY_KURGO ??
    process.env.GATSBY_KLAVIYO_PUBLIC_API_KEY ??
    'gT3Eag';

  if (locale?.toLowerCase() === GLOBAL_LOCALES.English.id) {
    return {
      publicKey: defaultPublicKey,
      newsletterListId: 'XuLi9L',
    };
  }

  console.warn('default hit: kurgoLocaleToKlaviyoMap', locale);
  return { publicKey: '', newsletterListId: '' };
}

function petsafeLocaleToKlaviyoMap(locale: string): KlaviyoPublicMap {
  const defaultPublicKey =
    process.env.SANITY_STUDIO_KLAVIYO_PUBLIC_API_KEY_PETSAFE ??
    process.env.GATSBY_KLAVIYO_PUBLIC_API_KEY ??
    'QZrSEc';

  const isProductionEnvironment =
    process.env.NODE_ENV === 'production' &&
    process.env.CONTEXT === 'production';

  switch (locale?.toLowerCase()) {
    case GLOBAL_LOCALES.English.id:
      return {
        publicKey: defaultPublicKey,
        newsletterListId: process.env.GATSBY_KLAVIYO_LIST_ID_EN || 'WT6jim',
      };
    case GLOBAL_LOCALES.Australia.id:
      return { publicKey: 'StUrAt', newsletterListId: 'ThnuYA' };
    case GLOBAL_LOCALES.EnglishUK.id:
      return { publicKey: 'XsUue4', newsletterListId: 'RYb5hq' };
    case GLOBAL_LOCALES.FrenchBelgium.id:
      return { publicKey: 'Xauxic', newsletterListId: 'WnNe39' };
    case GLOBAL_LOCALES.Spanish.id:
      return { publicKey: 'XmZiaW', newsletterListId: 'U6Q4tK' };
    case GLOBAL_LOCALES.French.id:
      return { publicKey: 'XMjZpz', newsletterListId: 'SdXk7x' };
    case GLOBAL_LOCALES.Germany.id:
      return { publicKey: 'QRyNY8', newsletterListId: 'UL77iR' };
    case GLOBAL_LOCALES.EnglishIreland.id:
      return { publicKey: 'RYQpCX', newsletterListId: 'UXRC82' };
    case GLOBAL_LOCALES.Italian.id:
      return { publicKey: 'Rg86Cd', newsletterListId: 'Yaw7vN' };
    case GLOBAL_LOCALES.EnglishNetherlands.id:
      return { publicKey: 'QUEj6k', newsletterListId: 'VueEcd' };
    case GLOBAL_LOCALES.GermanSwitzerland.id:
      return { publicKey: 'Tktfem', newsletterListId: 'WLDKAT' };
    case GLOBAL_LOCALES.EnglishCanada.id:
    case GLOBAL_LOCALES.FrenchCanada.id:
      return {
        publicKey: isProductionEnvironment ? 'XDFjfa' : 'Yxs2tg',
        newsletterListId: isProductionEnvironment ? 'RhrQBm' : 'S5phij',
      };
    default:
      console.warn('default hit: petsafeLocaleToKlaviyoMap', locale);
      return { publicKey: '', newsletterListId: '' };
  }
}

function sportdogLocaleToKlaviyoMap(locale: string): KlaviyoPublicMap {
  const defaultPublicKey =
    process.env.SANITY_STUDIO_KLAVIYO_PUBLIC_API_KEY_SPORTDOG ??
    process.env.GATSBY_KLAVIYO_PUBLIC_API_KEY ??
    'VtUtmM';

  switch (locale?.toLowerCase()) {
    case GLOBAL_LOCALES.English.id:
      return {
        publicKey: defaultPublicKey,
        newsletterListId: process.env.GATSBY_KLAVIYO_LIST_ID_EN || 'TKaQ6U',
      };
    case GLOBAL_LOCALES.Australia.id:
      return { publicKey: 'T3gdMq', newsletterListId: 'YwWSMj' };
    case GLOBAL_LOCALES.EnglishUK.id:
      return { publicKey: 'WGXZgf', newsletterListId: 'YgGBPg' };
    case GLOBAL_LOCALES.French.id:
      return { publicKey: 'SZAXGj', newsletterListId: 'UyJkcY' };
    default:
      console.warn('default hit: sportdogLocaleToKlaviyoMap', locale);
      return { publicKey: '', newsletterListId: '' };
  }
}

function ifbLocaleToKlaviyoMap(locale: string): KlaviyoPublicMap {
  const publicKeyByEnv =
    process.env.SANITY_STUDIO_KLAVIYO_PUBLIC_API_KEY_INVISIBLEFENCE ??
    process.env.GATSBY_KLAVIYO_PUBLIC_API_KEY ??
    'W4agk3';

  switch (locale?.toLowerCase()) {
    case GLOBAL_LOCALES.English.id:
    case GLOBAL_LOCALES.EnglishCanada.id:
      return {
        publicKey: publicKeyByEnv,
        newsletterListId: '',
      };
    default:
      console.warn('default hit: ifbLocaleToKlaviyoMap', locale);
      return { publicKey: '', newsletterListId: '' };
  }
}

export function localeToKlaviyoPublicMap(
  locale: string,
  site: BrandKeyLiteral
): KlaviyoPublicMap {
  switch (site) {
    case 'ifb':
    case 'ifbDev':
      return ifbLocaleToKlaviyoMap(locale);
    case 'petsafe':
      return petsafeLocaleToKlaviyoMap(locale);
    case 'sportdog':
      return sportdogLocaleToKlaviyoMap(locale);
    case 'kurgo':
      return kurgoLocaleToKlaviyoMap(locale);
    default:
      return { publicKey: '', newsletterListId: '' };
  }
}

/** Maps Sanity/site identifier to Klaviyo brand key. */
function siteIdentifierToBrand(siteIdentifier: string): BrandKeyLiteral {
  const normalized = (siteIdentifier ?? '').toLowerCase();
  if (
    normalized === 'invisiblefence' ||
    normalized === 'ifb' ||
    normalized === 'ifbdev'
  )
    return 'ifb';
  if (normalized === 'petsafe') return 'petsafe';
  if (normalized === 'sportdog') return 'sportdog';
  if (normalized === 'kurgo') return 'kurgo';
  return 'ifb';
}

/**
 * Returns Klaviyo company ID (public key) for a given site and locale.
 * Use this for Sanity Studio or any context where you have siteIdentifier + locale.
 */
export function getCompanyIdForSiteAndLocale(
  siteIdentifier: string,
  locale: string
): string {
  const brand = siteIdentifierToBrand(siteIdentifier);

  const map = localeToKlaviyoPublicMap(
    (locale ?? 'en-us').toLowerCase(),
    brand
  );

  return map.publicKey || '';
}

/**
 * Builds a Klaviyo profile object from the provided parameters.
 * Returns null if no valid identifiers are provided.
 */
function buildKlaviyoProfile(params: {
  email?: string;
  phoneNumber?: string;
  exchangeId?: string;
  extra?: KlaviyoIdentifyPayload;
}): KlaviyoIdentifyPayload | null {
  const { email, phoneNumber, exchangeId, extra } = params;
  if (!email && !phoneNumber && !exchangeId) return null;

  return {
    ...(email ? { $email: email } : {}),
    ...(phoneNumber ? { $phone_number: phoneNumber } : {}),
    ...(exchangeId ? { _kx: exchangeId } : {}),
    ...mapSessionMarketingParams(),
    ...extra,
  };
}

/**
 * Gets the Klaviyo SDK object from globalThis.
 * Returns null if SDK is not available or push method is missing.
 */
function getKlaviyoSdk(): { push?: (args: unknown[]) => void } | null {
  const k = (
    globalThis as unknown as {
      klaviyo?: { push?: (args: unknown[]) => void };
    }
  ).klaviyo;

  if (!k || typeof k.push !== 'function') {
    return null;
  }

  return k;
}

export function identifyKlaviyoProfile(params: {
  email?: string;
  phoneNumber?: string;
  exchangeId?: string;
  extra?: KlaviyoIdentifyPayload;
}): void {
  if (!isPixelSupported(PIXELS.klaviyo)) return;

  const profile = buildKlaviyoProfile(params);
  if (!profile) return;

  waitForKlaviyo(() => {
    const k = getKlaviyoSdk();
    if (!k) {
      console.warn('[klaviyo.identify] SDK push unavailable');
      return;
    }
    try {
      k.push(['identify', profile]);
    } catch (error) {
      console.error('[klaviyo.identify] failed', error);
    }
  });
}

export async function identifyKlaviyoProfileAsync(params: {
  email?: string;
  phoneNumber?: string;
  exchangeId?: string;
  extra?: KlaviyoIdentifyPayload;
}): Promise<void> {
  if (!isPixelSupported(PIXELS.klaviyo)) {
    throw new Error('Klaviyo pixel not supported');
  }

  const profile = buildKlaviyoProfile(params);
  if (!profile) {
    throw new Error(
      'No identifier (email, phone, or exchangeId) provided for Klaviyo identification'
    );
  }

  return new Promise<void>((resolve, reject) => {
    const timeout = 2000; // 2 second timeout for async version
    const start = Date.now();

    const tryIdentify = (): void => {
      const k = getKlaviyoSdk();

      if (k) {
        try {
          k.push(['identify', profile]);
          resolve();
        } catch (error) {
          console.error('[klaviyo.identifyAsync] failed', error);
          reject(error);
        }
      } else if (Date.now() - start > timeout) {
        console.warn('[klaviyo.identifyAsync] SDK not available after timeout');
        reject(new Error('Klaviyo SDK not available after timeout'));
      } else {
        // Retry after 100ms
        setTimeout(tryIdentify, 100);
      }
    };

    tryIdentify();
  });
}

export function track(
  name: TrackEventName,
  payload: unknown,
  wait: number = 2000
): void {
  if (!isPixelSupported(PIXELS.klaviyo)) {
    return;
  }
  waitForKlaviyo(() => {
    const k = (
      globalThis as unknown as {
        klaviyo?: {
          track?: (name: string, properties: Record<string, unknown>) => void;
        };
      }
    ).klaviyo;
    if (k && typeof k.track === 'function') {
      k.track(name, payload as Record<string, unknown>);
    } else {
      console.warn(
        `[IframeTracking] Klaviyo SDK has no track function for "${name}".`
      );
    }
  }, wait);
}

export function trackSearchViewedEvent(
  searchTerm: string,
  resultCount: number,
  locale: string = 'en-us'
): void {
  const payload: SearchViewedPayload = {
    SearchTerm: searchTerm,
    ResultCount: resultCount,
    Timestamp: new Date().toISOString(),
    locale,
  };
  track('Search Viewed', payload);
}

export function trackViewedProductEvent(p: ViewedProductPayload): void {
  track('Viewed Product', p);
}

export function trackViewedCategoryEvent(p: ViewedCategoryPayload): void {
  track('Viewed Category', p);
}

export function trackAddedToCartEvent(p: AddedToCartPayload): void {
  track('Added to Cart', p);
}

export function trackViewedPageEvent(p: ViewedPagePayload): void {
  track('Viewed Page', p);
}

export function trackBackInStockInterestEvent({
  email,
  ProductID,
  SKU,
  ProductName,
  ProductURL,
  Price,
  VariantName,
  VariantID,
  Locale,
}: BackInStockInterestPayload): Promise<void> {
  if (!isPixelSupported(PIXELS.klaviyo)) {
    return Promise.resolve();
  }

  // First identify the user as required by Klaviyo documentation
  return identifyKlaviyoProfileAsync({
    email,
  })
    .then(() => {
      // Add a small delay to ensure Klaviyo has processed the identify call
      // before queuing the track event
      return new Promise<void>(resolve => {
        setTimeout(() => {
          // Then track the event only after successful identification
          track('Back in Stock Interest', {
            email,
            ProductID,
            SKU,
            ProductName,
            ProductURL,
            Price,
            VariantName,
            VariantID,
            Locale,
          });
          resolve();
        }, 500); // 500ms delay to ensure identify is processed
      });
    })
    .catch(error => {
      console.error(
        '[Klaviyo] Failed to identify profile for Back in Stock Interest:',
        error
      );
    });
}

/**
 * patchKlaviyoTeaserAccessibility
 *
 * Klaviyo’s embedded “teaser” (the small floating Spin-to-Win button)
 * renders asynchronously and includes markup that breaks accessibility:
 *  - The outer teaser container is given `role="button"` and `tabindex="0"`,
 *    which traps focus and confuses screen readers.
 *  - The teaser’s visual “open” area isn’t keyboard focusable.
 *  - The close button isn’t fully keyboard accessible (Enter/Space don’t trigger it).
 *
 * This function corrects those issues by:
 *  1. Removing problematic role/tabindex attributes from the teaser wrapper.
 *  2. Ensuring the close button can be focused and activated by keyboard.
 *  3. Ensuring the teaser "open" area is focusable and behaves like a button.
 *  4. Re-running these fixes when Klaviyo re-inserts DOM nodes.
 */
export function patchKlaviyoTeaserAccessibility(): void {
  const fixKlaviyoAccessibility = (): void => {
    // Find all Klaviyo teaser containers (small floating “Spin to Win” widgets)
    const teasers = document.querySelectorAll<HTMLElement>(
      '[class*="kl-teaser-"]'
    );

    for (const teaserEl of teasers) {
      const teaser = teaserEl;
      if (teaser.dataset.accessibilityFixApplied) return;
      teaser.dataset.accessibilityFixApplied = 'true';

      // Remove outer role/tabindex (to avoid trapping focus)
      // They make the entire teaser act like one big button,
      // trapping focus and confusing screen readers.
      teaser.removeAttribute('role');
      teaser.removeAttribute('tabindex');
    }

    // Close button fix
    const closeBtn = document.querySelector<HTMLButtonElement>(
      'button.klaviyo-close-form[aria-label="Close teaser"]'
    );
    if (closeBtn && !closeBtn.dataset.keyboardFixApplied) {
      closeBtn.dataset.keyboardFixApplied = 'true';
      closeBtn.setAttribute('tabindex', '0');
      closeBtn.setAttribute('aria-label', 'Close teaser');

      closeBtn.addEventListener('keydown', (event: KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          event.stopPropagation();
          closeBtn.click();
        }
      });

      // Stop teaser open logic when clicking close
      closeBtn.addEventListener('click', (event: MouseEvent) => {
        event.stopPropagation();
      });
    }

    // Find teaser open element (the visible clickable area)
    const openBtn = document.querySelector<HTMLElement>(
      'span[class*="needsclick Teaser-pointer-"]'
    );

    if (openBtn && !openBtn.dataset.keyboardFixApplied) {
      openBtn.setAttribute('tabindex', '0');
      openBtn.setAttribute('role', 'button');
      openBtn.setAttribute('aria-label', 'Open teaser');

      if (!openBtn.dataset.keyboardFixApplied) {
        openBtn.dataset.keyboardFixApplied = 'true';

        // Enable keyboard open
        openBtn.addEventListener('keydown', (event: KeyboardEvent) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            event.stopPropagation();

            // Simulate a user click on the teaser open element itself
            openBtn.click();
          }
        });
      }
    }
  };

  fixKlaviyoAccessibility();
  setTimeout(fixKlaviyoAccessibility, 1500);
  const observer = new MutationObserver(fixKlaviyoAccessibility);
  observer.observe(document.body, { childList: true, subtree: true });
}

export async function sendKlaviyoSubscription({
  formId,
  formVersionId,
  email,
  phoneNumber,
  pageUrl,
  listId,
  customSource,
  successStepName,
  recaptchaToken,
  locale,
  values,
}: {
  formId: string;
  formVersionId?: string | number;
  formVersionName?: string;
  email?: string;
  phoneNumber?: string;
  pageUrl?: string;
  listId?: string;
  customSource?: string;
  successStepName?: string;
  recaptchaToken?: string;
  locale?: string;
  values?: Record<string, unknown>;
}) {
  const resolvedLocale = (locale || 'en-us').toLowerCase();

  const url = buildKlaviyoApiUrl('subscribe');
  const body = {
    values: {
      ...mapSessionMarketingParams(),
      ...values,
      $email: email,
      $phone_number: phoneNumber,
      $consent_method: 'Klaviyo Form',
      $consent_form_id: formId,
      $consent_form_version: formVersionId,
      $success_step_name: successStepName,
    },
    listId,
    formId,
    formVersion: formVersionId,
    locale: resolvedLocale,
    recaptchaToken,
    pageUrl,
    customSource: customSource ?? 'Klaviyo Form',
  };

  const res = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    let responseText: string | undefined;
    let detail: string | undefined;

    try {
      responseText = await res.text();
    } catch (err) {
      console.warn('[Klaviyo] Failed to read subscription error response', err);
    }

    if (responseText) {
      try {
        const parsed = JSON.parse(responseText);
        detail =
          parsed?.error ||
          parsed?.message ||
          parsed?.errors?.[0]?.detail ||
          parsed?.errors?.[0]?.title;
      } catch (err) {
        console.warn(
          '[Klaviyo] Failed to parse subscription error response',
          err
        );
      }
    }

    const message =
      detail ??
      (responseText
        ? `${responseText}`
        : `Klaviyo subscription failed: ${res.status}`);

    console.error('[Klaviyo] subscription failed', {
      status: res.status,
      detail,
      responseText,
    });

    throw new Error(message);
  }

  const data = await res.json().catch(() => ({}));
  return data;
}

export interface ProfileDataCaptureDealerProcessSlot {
  appointmentId: string;
  appointmentGrade: number;
  start: string;
  finish: string;
  startDate: string;
  endDate: string;
  slotDisplay: string;
  timeZone: string;
  workOrderId: string;
  earlierAppointmentDesired: boolean;
  exactAppointment: boolean;
  workTypeId: string;
  workTypeName: string;
}

export interface ProfileDataCaptureDealerProcess {
  processStatus: CustomerProcessStatusEnum;
  contactId?: string;
  workTypes: Array<{
    id?: string;
    customerLabel?: string;
    developerName?: string;
    isDefaultWorkType: boolean;
    isOnsiteConsult?: boolean;
    isRemoteConsult?: boolean;
    isExactAppointment?: boolean;
    getApptsLoadTime?: number;
    createApptAvgTimeMS?: number;
    slotCount?: number;
    appointmentSlots: Record<string, ProfileDataCaptureDealerProcessSlot[]>;
  }>;
}

export interface ProfileDataCaptureDealer {
  id: string;
  name: string;
  phone?: string;
  email?: string;
  mainServiceArea?: string;
  serviceAreas?: string;
  subdomain?: string;
  isCod?: boolean;
  isCrm?: boolean;
  isEFence?: boolean;
  isEmail?: boolean;
  dealerType?: string;
  isOnlineScheduling?: boolean;
  isInHomeConsult?: boolean;
  isVirtualConsult?: boolean;
  dealerWebsite?: string;
  process?: ProfileDataCaptureDealerProcess | null;
}

export interface ProfileDataCaptureKlaviyoProfileAttributes {
  email?: string;
  phone_number?: string;
  first_name?: string;
  last_name?: string;
  location?: {
    address1?: string;
    city?: string;
    region?: string;
    zip?: string;
    country?: string;
  };
}

export interface ProfileDataCaptureKlaviyoProfile {
  type?: string;
  id?: string;
  attributes?: ProfileDataCaptureKlaviyoProfileAttributes;
}

export interface ProfileDataCaptureResponse {
  sessionId: string;
  clientId: string;
  dealers: ProfileDataCaptureDealer[];
  klaviyoProfile: ProfileDataCaptureKlaviyoProfile;
}

export async function sendProfileDataCapture({
  exchangeId,
  recaptchaToken,
  locale,
}: {
  exchangeId: string;
  recaptchaToken: string;
  locale?: string;
}): Promise<ProfileDataCaptureResponse> {
  const apiBase = getKlaviyoApiBase();
  const brand = getKlaviyoBrand();
  const resolvedLocale = (locale || 'en-us').toLowerCase();

  const url = `${apiBase}/profileDataCapture?locale=${resolvedLocale}&brand=${brand}&exchangeId=${encodeURIComponent(exchangeId)}`;

  const params =
    typeof globalThis.location?.search === 'string'
      ? new URLSearchParams(globalThis.location.search)
      : new URLSearchParams();

  const body = {
    referredByCode: params.get('referral_code') ?? '',
    clientId: params.get('clientId') ?? '',
    sourceSystem: 'shopify-invisible-fence',
    pageUri: globalThis.location?.href ?? '',
    marketingPromo: params.get('marketingPromo') ?? '',
    utmSource: params.get('utm_source') ?? '',
    utmMedium: params.get('utm_medium') ?? '',
    utmCampaign: params.get('utm_campaign') ?? '',
    hubspotToken: params.get('hubspotToken') ?? '',
    recaptchaToken,
  };

  const res = await fetch(url, {
    method: 'POST',
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `profileDataCapture failed: ${res.status}`);
  }

  return res.json();
}
